import React from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { Button, Textarea } from '@headlessui/react'
import { X } from 'lucide-react'
import { CustomSelect } from 'components/common/CustomSelect'
import { LoaderButton } from 'components/common/LoadingButton'
import FormInput from 'components/common/FormInput'
import CustomCalendar from 'components/common/CustomCalendar'
import { CrmTask, priorityOptions, TaskFormValues, taskStatusOptions, taskTypeOptions } from 'types/crm/task'
import { taskFormInitialValues, taskFormValidationSchema } from 'utils/validation'
import OwnerSearchDropdown from '../Dropdowns/OwnerSearchDropdown'
import { formatDateToYmd } from 'utils/date'
import FormTextarea from 'components/common/FormTextArea'

type Owners = React.ComponentProps<typeof OwnerSearchDropdown>['owners']


/** Maps an existing task onto form shape, or falls back to blank defaults for create. */
const getInitialValues = (task?: CrmTask | null): TaskFormValues => {
    if (!task) return taskFormInitialValues

    return {
        ...taskFormInitialValues,
        title: task.title ?? '',
        description: task.description ?? '',
        taskType: task.taskType ?? taskFormInitialValues.taskType,
        priority: task.priority ?? taskFormInitialValues.priority,
        taskStatus:task.taskStatus ?? taskFormInitialValues.taskStatus,
        taskOwnerUuid: task.taskOwner?.uuid ?? null,
        dueAt: task.dueAt ? formatDateToYmd(new Date(task.dueAt)) : '',
    }
}

export interface CreateTaskProps {
    /** UUID of the record this task belongs to (contact, deal, company, ...) */
    entityUuid: string
    owners: Owners
    onCancel: () => void
    onSubmit: (
        values: TaskFormValues,
        helpers: { resetForm: () => void },
    ) => void | Promise<void>
    /** Optional ref if the parent needs to imperatively reset the form (e.g. on tab switch) */
    formikRef?: React.Ref<FormikProps<TaskFormValues>>
    isSubmitting?: boolean
    title?: string
    /** Pass the task being edited to pre-fill the form; omit/null for create mode. */
    task?: CrmTask | null
}

const CreateTask = ({
    entityUuid,
    owners,
    onCancel,
    onSubmit,
    formikRef,
    isSubmitting = false,
    title,
    task = null,
}: CreateTaskProps) => {
    const isEditing = !!task
    const heading = title ?? (isEditing ? 'Edit Task' : 'Add Task')

    return (
        <Formik<TaskFormValues>
            innerRef={formikRef}
            initialValues={getInitialValues(task)}
            enableReinitialize
            validationSchema={taskFormValidationSchema}
            onSubmit={onSubmit}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                resetForm,
                isValid,
                dirty
            }: FormikProps<TaskFormValues>) => {
                const handleCancel = () => {
                    resetForm()
                    onCancel()
                }

                return (
                    <Form onSubmit={handleSubmit}>
                        <div className="rounded-xl bg-primary-light border border-primary-subtle p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-neutral font-medium">{heading}</h3>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="text-neutral-faded hover:text-neutral"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="flex flex-col gap-5">
                                <FormInput
                                    label="Title"
                                    name="title"
                                    value={values.title}
                                    placeholder="e.g. Send updated proposal"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.title && errors.title}
                                    darkBg={true}
                                    whiteLabel={false}
                                    required
                                />

                                {/* <div>
                                    <label className="text-sm text-neutral-faded block mb-2">
                                        Description
                                    </label>
                                    <Textarea
                                        name="description"
                                        value={values.description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Add more detail"
                                        rows={3}
                                        className="w-full rounded-lg bg-inherit border border-primary-subtle p-3 text-sm text-neutral placeholder:font-normal placeholder:text-base placeholder:text-[#374151] focus:outline-none  resize-none transition-colors"
                                    />
                                </div> */}
                                <FormTextarea
                                name="description"
                                label="Description"
                                placeholder="Add more detail"
                                maxLength={500}
                                // darkBg
                                className={{ label: 'text-sm', textarea: 'text-sm' }}
                            />


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <CustomSelect
                                        label="Task Type"
                                        name="taskType"
                                        options={taskTypeOptions}
                                        value={values.taskType}
                                        onChange={(val: string) => setFieldValue('taskType', val)}
                                        onBlur={handleBlur}
                                        placeholder='Select Task Type'
                                        error={touched.taskType && errors.taskType}
                                        whiteLabel={false}
                                        darkBg
                                        required
                                    />
                                    <CustomSelect
                                        label="Priority"
                                        name="priority"
                                        options={priorityOptions}
                                        value={values.priority}
                                        onChange={(val: string) => setFieldValue('priority', val)}
                                        onBlur={handleBlur}
                                        placeholder='Select Priority'
                                        error={touched.priority && errors.priority}
                                        whiteLabel={false}
                                        darkBg
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <CustomSelect
                                        label="Task Status"
                                        name="taskStatus"
                                        options={taskStatusOptions}
                                        value={values.taskStatus}
                                        onBlur={handleBlur}
                                        onChange={(val: string) => setFieldValue('taskStatus', val)}
                                        placeholder="Select Task Status"
                                        error={touched.taskStatus && errors.taskStatus}
                                        whiteLabel={false}
                                        darkBg
                                        required
                                    />

                                    <CustomCalendar
                                        label="Due Date"
                                        placeholder="mm/dd/yyyy"
                                        value={values.dueAt ? new Date(values.dueAt) : null}
                                        className="bg-inherit"
                                        onChange={(date) => {
                                            setFieldValue('dueAt', formatDateToYmd(date))
                                        }}
                                        minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-neutral-faded block mb-2">
                                        Assign To
                                    </label>
                                    <OwnerSearchDropdown
                                        owners={owners}
                                        currentOwnerUuid={values.taskOwnerUuid}
                                        onOwnerChange={(owner) =>
                                            setFieldValue('taskOwnerUuid', owner?.uuid ?? null)
                                        }
                                        placeholder="Select Assignee"
                                        allowUnassigned={true}
                                        unassignedLabel="None"
                                        size="md"
                                        className="w-full bg-inherit border border-primary-subtle rounded-lg"
                                    />
                                </div>

                            </div>

                            <div className="flex items-center justify-end gap-3 mt-6">
                                <Button
                                    type="button"
                                    onClick={handleCancel}
                                    className="py-2 px-4 rounded-lg border border-primary-subtle text-neutral text-sm font-medium hover:bg-primary-surface transition-colors"
                                >
                                    Cancel
                                </Button>
                                <LoaderButton
                                    type="submit"
                                    isLoading={isSubmitting}
                                    text={isEditing ? 'Save changes' : 'Create task'}
                                    className="!mt-0"
                                    disabled={isSubmitting || !dirty || !isValid}
                                />
                            </div>
                        </div>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default CreateTask